import { Component, ElementRef, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import { Vec2, VectorMath } from './vector-math';

/** An event emitted by {@link ImageCropperComponent.cropChanged}. */
export type ImageCropChangedEvent = {
  /** The cropped image data as a Data URI. This will be a PNG image. */
  dataUri: string;
}

type AnyMouseEvent = DragEvent | TouchEvent | MouseEvent;

/**
 * This image cropper is designed to let the user pick out a part of the image to crop down to and use elsewhere.
 *
 * Its initial use is for cropping a photo down to the part that focuses on someone's face, for use as an avatar.
 *
 * This image cropper works on the following basic ideas:
 * - We have an image fed into the image cropper. We know its objective size, e.g. this is a 1080 x 720 photo.
 * - We have a selection, e.g. a 200x200 box.
 * - We apply that selection to the image in order to crop it.
 * - The HTML/CSS gives us a view of the image centered on that selection.
 * - When we drag and zoom, we're moving that selection around proportionate to its size on screen.
 *
 * Essentially, we are clicking and dragging around a selection box on an image.
 * However, our view is fixed on that selection box.
 *
 * Notes:
 * - This image cropper is currently made for square images, and doesn't adjust for rectangular images.
 *
 * @see {@link https://github.com/bcabanes/angular-image-cropper/blob/master/src/imageCropper.js} —
 *      This cropper was partially based on bcabane's AngularJS image cropper.
 */
@Component({
  selector: 'app-image-cropper',
  templateUrl: './image-cropper.component.html',
  styleUrl: './image-cropper.component.scss',
  standalone: false
})
export class ImageCropperComponent {
  /** The source image to use. */
  @Input() public source = "";
  @Output() public cropChanged = new EventEmitter<ImageCropChangedEvent>(false);
  @ViewChild("innerBox", { static: true }) public innerBoxRef: ElementRef<HTMLDivElement> | undefined;
  @ViewChild("image", { static: true }) public imageRef: ElementRef<HTMLImageElement> | undefined;
  @ViewChild("output", { static: true }) public outputRef: ElementRef<HTMLCanvasElement> | undefined;

  /** The smallest the selection is allowed to get, proportionately. */
  private readonly MINIMUM_SCALE = 0.1;

  /** The initial zoom adjustment to apply to the image. This zooms it in just a little bit. */
  private readonly INITIAL_ZOOM = 0.9;

  /**
   * The zoom adjustment to apply on zoom in/out.
   *
   * Later we might want to do something smarter, like zoom in by a value proportionate to some percentage of the image size.
   */
  private readonly ZOOM_ADJUSTMENT = 0.2;

  /**
   * The size of the image selection (unscaled).
   *
   * This also represents the size of the crop that will be produced.
   */
  public selectionSize: Vec2 = { x: 300, y: 300 };
  /** The top left position of the image selection. */
  public selectionPosition: Vec2 = { x: 0, y: 0 };
  /**
   * The scale of the image selection.
  *
  * A smaller scale means the selection within the image gets smaller. That means we're zooming in.
  * A larger scale means the selection within the iamge gets larger. That means we're zooming out.
  */
  public selectionScale = 1;
  public get selectionScaledSize() {
    return VectorMath.multiply(this.selectionSize, this.selectionScale);
  }

  /** True if the user is currently doing a drag operation. */
  private dragging = false;
  /** The last known pointer position, tracked during panning. */
  private pointerPosition: Vec2 = { x: 0, y: 0 };
  /** The initial zoom state, tracked for calculating zooms. */
  private zoomState: { initialDistance: number, initialScale: number } | undefined = undefined;

  /** True if an image has been loaded in at all yet. */
  public imageLoaded = false;

  /**
    * The ratio of pixels seen on the screen : pixels in the image.
    */
  public get pixelRatio() {
      const imageScale = 1 / this.selectionScale;
      const boxScale = this.boxScale;
      return imageScale * boxScale;
  }

  /**
   * The CSS style object to apply to the image in HTML.
   */
  public get imageStyle() {
    const scale = this.pixelRatio;

    const style = {
      width: this.selectionSize.x * scale,
      height: this.selectionSize.y * scale,
      position: 'relative',
      transformOrigin: 'top left',
      transform: [
        `scale(${scale})`,
        `translate(${-1 * this.selectionPosition.x}px, ${-1 * this.selectionPosition.y}px)`,
      ].join(' '),
    }
    return style;
  }

  /** The size of the cropping box. */
  private get boxSize(): Vec2 {
    return {
      x: this.innerBoxRef?.nativeElement.offsetWidth ?? 0,
      y: this.innerBoxRef?.nativeElement.offsetHeight ?? 0,
    }
  }

  /** The scale of the box, relative to the selection crop. */
  private get boxScale() {
    return this.boxSize.x / this.selectionSize.x;
  }

  /** The actual size of the image being displayed, regardless of scaling. */
  public get imageSize(): Vec2 {
    return {
      x: this.imageRef?.nativeElement.width ?? 0,
      y: this.imageRef?.nativeElement.height ?? 0,
    }
  }

  /** Reset the cropper to an initial state. */
  private reset() {
    /** Zoom in slightly compared to a perfect cover. */
    const imageSize = this.imageSize;
    const coverScale = this.getCoverScale();
    this.selectionScale = coverScale * this.INITIAL_ZOOM;
    this.centerSelectionOn({ x: imageSize.x / 2, y: imageSize.y / 2 });
    this.paintCrop();
  }

  public handleImageLoad() {
    this.reset();
    this.imageLoaded = true;
  }

  public handleDragStart(event: AnyMouseEvent) {
    event.preventDefault();
    event.stopPropagation();
    this.dragging = true;
    this.handlePinchZoom(event);
    this.pointerPosition = this.getPointerPosition(event);
  }

  public handleDragEnd(event: AnyMouseEvent) {
    event.preventDefault();
    event.stopPropagation();

    if (event instanceof TouchEvent) {
      if (event.touches.length > 0) {
        // There's still a touch on the image cropper. This is just one of the fingers lifting off.
        return;
      }
    }

    this.dragging = false;
    this.handlePinchZoomEnd();
    this.paintCrop();
  }

  public handleDragging(event: AnyMouseEvent) {
    event.preventDefault();
    event.stopPropagation();

    if (!this.dragging) {
      return;
    }

    this.handlePinchZoom(event);
    this.handlePan(event);
  }

  /**
   * Handle panning the image around with the cursor.
   *
   * @param event The user event
   */
  private handlePan(event: AnyMouseEvent) {
    // The cursor position
    const p = this.getPointerPosition(event);

    // The difference compared to the last known cursor position
    const diff = VectorMath.subtract(this.pointerPosition, p);

    this.pointerPosition = p; // Update cursor position.

    const movement = VectorMath.multiply(diff, 1 / this.pixelRatio);
    const position = VectorMath.add(this.selectionPosition, movement);
    this.setSelectionPosition(position);
  }

  /**
   * Handle pinch to zoom, meanig using two fingers to zoom in and out.
   *
   * @param event The user event
   */
  private handlePinchZoom(event: AnyMouseEvent) {
    if (!(event instanceof TouchEvent)) {
      return;
    }

    if (event.touches.length < 2 && this.zoomState) {
      this.handlePinchZoomEnd();
      return;
    }

    const touch1 = this.getTouchPosition(event.touches.item(0));
    const touch2 = this.getTouchPosition(event.touches.item(1));

    if (!this.zoomState) {
      this.handlePinchZoomStart(touch1, touch2)
      // This pinch zoom has only just started. No need to do any scale calculations.
      return;
    }

    const { initialDistance, initialScale } = this.zoomState;
    const distance = VectorMath.distanceBetween(touch1, touch2);
    // If initial was 1, and distance is now 2, we're dragging to zoom in. This will equal 2.
    // If initial was 2, and distance is now 1, we're dragging to zoom out. This will equal 0.5.
    const proportion = (distance / initialDistance);
    // However, a zoom of 2 means we should set the scale to 0.5 of what it was, and vice versa.
    // So we need to invert the proportion before we apply it to the scale.
    const invertedProportion = 1 / proportion

    this.setScale(initialScale * invertedProportion);
  }

  private handlePinchZoomStart(touch1: Vec2, touch2: Vec2) {
    this.zoomState = { initialDistance: VectorMath.distanceBetween(touch1, touch2), initialScale: this.selectionScale };
  }

  private handlePinchZoomEnd() {
    this.zoomState = undefined;
    this.paintCrop();
  }

  /**
   * Zoom in on the picture.
   *
   * This reduces the selection's scale.
   */
  public zoomIn() {
    const center = this.getSelectionCenter();
    this.setScale(this.selectionScale - this.ZOOM_ADJUSTMENT);
    this.centerSelectionOn(center);
  }

  /**
   * Zoom out on the picture.
   *
   * This increases the selection's scale.
   */
  public zoomOut() {
    const center = this.getSelectionCenter();
    this.setScale(this.selectionScale + this.ZOOM_ADJUSTMENT);
    this.centerSelectionOn(center);
  }

  /**
   * Set the scale to a given number.
   *
   * This will ensure the scale remains clamped within an allowable range.
   *
   * @param value The scale to set
   */
  private setScale(value: number) {
    if (value < this.MINIMUM_SCALE) {
      value = this.MINIMUM_SCALE;
    }

    const coverScale = this.getCoverScale();
    if (value > coverScale) {
      value = coverScale;
    }

    this.selectionScale = value;
    this.clampPosition();
  }

  private setSelectionPosition(value: Vec2) {
    this.selectionPosition = value;
    this.clampPosition();
  }

  /**
   * Calculate the {@link selectionScale} that would cause the selection to fit the exact height or width of the image, whichever's smaller.
   *
   * The scale should never be allowed to be greater than this value. You cannot have a selection that goes outside the image.
   *
   * Example: if this is a 150w by 500h image, this would calculate the scale that makes the selection 150 wide.
   */
  private getCoverScale() {
    const imageSize = this.imageSize;
    const selectionSize = this.selectionSize;

    const xCoverScale = imageSize.x / selectionSize.x;
    const yCoverScale = imageSize.y / selectionSize.y;
    const coverScale = Math.min(xCoverScale, yCoverScale);
    return coverScale;
  }


  /**
   * Paint a crop to the image output canvas.
   *
   * This will emit the new crop.
   */
  private paintCrop() {
    if (!this.outputRef || !this.imageRef) {
      throw Error("Can't paintCrop(): outputRef or imageRef is missing")
    }
    const canvas = this.outputRef.nativeElement;
    const ctx = canvas.getContext("2d");
    if (!ctx) {
      throw Error("Can't paintCrop(): outputRef or imageRef is missing")
    }
    ctx.fillStyle = "black";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(this.imageRef.nativeElement,
      this.selectionPosition.x,
      this.selectionPosition.y,
      this.selectionScaledSize.x,
      this.selectionScaledSize.y,
      0,
      0,
      canvas.width,
      canvas.height,
    );
    const dataUri = canvas.toDataURL("image/png");
    this.cropChanged.emit({ dataUri })
  }

  /**
   * Center the selection on a specific point.
   * @param center The point to center on
   */
  private centerSelectionOn(center: Vec2) {
    const halfsize = VectorMath.divide(this.selectionScaledSize, 2);
    const position = VectorMath.subtract(center, halfsize);
    this.setSelectionPosition(position);
  }

  /**
   * Get the position that represents the center of the current selection.
   *
   * You may want this to re-center the canvas later with {@link centerSelectionOn}.
   */
  private getSelectionCenter(): Vec2 {
    const halfsize = VectorMath.divide(this.selectionScaledSize, 2);
    return VectorMath.add(this.selectionPosition, halfsize);
  }

  /** Get the primary pointer position of the event. */
  private getPointerPosition(event: AnyMouseEvent): Vec2 {
    if (event instanceof TouchEvent) {
      return this.getTouchPosition(event.touches.item(0));
    }
    return { x: event.pageX, y: event.pageY };
  }

  /** Get the position of a particular Touch. */
  private getTouchPosition(touch: Touch | null): Vec2 {
    return { x: touch?.pageX ?? -1, y: touch?.pageY ?? -1 };
  }

  /**
   * Do a check to clamp the selection position within the bounds of the image.
   *
   * Do this after any scale changes.
   */
  private clampPosition() {
    let { x, y } = this.selectionPosition;

    // Constrain lower bounds
    x = Math.max(x, 0);
    y = Math.max(y, 0);

    // Constrain upper bounds
    const selectionScaledSize = this.selectionScaledSize;
    const imageSize = this.imageSize;
    x = Math.min(x, imageSize.x - selectionScaledSize.x);
    y = Math.min(y, imageSize.y - selectionScaledSize.y);

    this.selectionPosition = { x, y };
  }

  /** Stringify vectors and numbers for debug output. */
  public stringify(value: any) {
    if (typeof value === "object" && "x" in value && "y" in value) {
      return `{x: ${value.x}, y: ${value.y}}`;
    }
    return String(value);
  }
}
